<template>
  <div class="ai-assistant-permission" :class="{ 'ai-assistant-permission--init': isInit }">
    <div v-if="!isInit" class="title">AI 助理人员权限管理</div>
    <div class="desc-card">
      <div class="desc-card__field">
        <div class="desc-card__label">已授权人员/可授权人员总数</div>
        <div class="desc-card__value">
          <span class="desc-card__value-current">{{ hasAuthorize }}</span>
          <span class="desc-card__value-split">/</span>
          <span class="desc-card__value-total">{{ totalCount }}</span>
        </div>
      </div>
      <div class="desc-card__progress-track">
        <div class="desc-card__progress-bar" :style="{ width: `${progressPercent}%` }"></div>
      </div>
    </div>

    <div class="condition">
      <div class="condition__filters">
        <div class="condition__item">
          <ai-condition label="部门">
            <template #content>
              <department-select
                v-model:value="selectDepartId"
                @departChange="handleDepartmentChange"
              ></department-select>
            </template>
          </ai-condition>
        </div>
        <div class="condition__item">
          <ai-condition label="">
            <template #content>
              <UserSelect
                v-model:value="userId"
                placeholder="搜索员工名称"
                @selectUser="handleUserChange"
              ></UserSelect>
            </template>
          </ai-condition>
        </div>
      </div>
      <el-button
        class="condition__action"
        color="#7a52f4"
        type="primary"
        @click="openPersonSelectDialog"
        >+ 添加授权人员
      </el-button>
    </div>

    <div class="permission-list">
      <div class="permission-list__table-wrap">
        <el-table
          v-loading="listLoading"
          cell-class-name="permission-list__cell"
          class="permission-list__table"
          :data="permissionTableData"
          header-cell-class-name="permission-list__header-cell"
          height="100%"
        >
          <el-table-column label="员工名称" min-width="120" prop="name" />
          <el-table-column label="角色" min-width="120" prop="roleConcat" />
          <el-table-column label="操作" width="120">
            <template #default="scope">
              <el-button
                class="permission-list__cancel-btn"
                link
                type="primary"
                @click="openCancelConfirm(scope.row)"
              >
                取消授权
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="permission-list__pagination">
        <el-pagination
          :current-page="currentPage"
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="total"
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </div>

  <Dialog v-model="confirmVisible" align-center title="取消授权" width="400px">
    <i class="icon-error-warning-line t-iconfont" style="color: #ffb521; margin-right: 10px" />
    <span>确认取消授权吗？</span>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleConfirmCancel">取 消</el-button>
        <el-button :loading="cancelLoading" type="primary" @click="handleConfirmPermissionCancel"
          >确 认</el-button
        >
      </div>
    </template>
  </Dialog>

  <PersonSelect
    v-model="selectedUsers"
    v-model:show="personSelectVisible"
    :is-license="1"
    :max="totalCount - hasAuthorize"
    :show-department="false"
    @confirm="handlePersonSelectConfirm"
  />
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import AiCondition from '@/components/ai-condition/index.vue'
import Dialog from '@/components/ai-dialog/index.vue'
import PersonSelect from '@/components/ai-person-select-v2/person-select.vue'
import DepartmentSelect from '@/pages/ai-board/components/components/department-select.vue'
import UserSelect from '@/pages/ai-board/components/components/user-select.vue'
import {
  getFeeLogDetail,
  getLicenseList,
  deleteLicense,
  addLicense,
  hasRoleEdit
} from '@/api/ai-setting'

defineProps({
  isInit: {
    type: Boolean,
    default: false
  }
})

const totalCount = ref(0)

const selectDepartId = ref('')
const userId = ref('')
const nameLike = ref('')
const currentPage = ref(1)
const pageSize = 20
const total = ref(0)
const hasAuthorize = ref(0)
const permissionTableData = ref([])
const listLoading = ref(false)
const confirmVisible = ref(false)
const cancelLoading = ref(false)
const pendingCancelUser = ref(null)
const aiLicenseManagementEdit = ref(false)
const selectedUsers = ref([])
const personSelectVisible = ref(false)

const getPermissionList = async () => {
  listLoading.value = true
  try {
    const res = await getLicenseList({
      departmentId: selectDepartId.value,
      nameLike: nameLike.value,
      page: currentPage.value,
      pageSize
    })
    const result = res?.result || {}
    permissionTableData.value = result.userList || []
    total.value = result.pageHelper.rowsCount
  } finally {
    listLoading.value = false
  }
}

const handleDepartmentChange = (departmentId) => {
  selectDepartId.value = departmentId || ''
  currentPage.value = 1
  getPermissionList()
}

const handleUserChange = (user) => {
  userId.value = user?.userId || ''
  nameLike.value = user?.name || ''
  currentPage.value = 1
  getPermissionList()
}

const progressPercent = computed(() => {
  if (!totalCount.value) {
    return 0
  }

  const percent = (hasAuthorize.value / totalCount.value) * 100

  return Math.min(Math.max(percent, 0), 100)
})
const showNoPermissionMessage = () => {
  ElMessage.warning('暂无权限')
}

/**
 * 打开绑定成员对话框
 */
const openPersonSelectDialog = () => {
  if (!aiLicenseManagementEdit.value) {
    showNoPermissionMessage()
    return
  }

  selectedUsers.value = []
  personSelectVisible.value = true
}
const handleAddPermission = async (data) => {
  const userIdList = (data || []).map((item) => item.id).filter(Boolean)

  if (!userIdList.length) {
    return
  }

  await addLicense({
    license: 74,
    userIdList
  })
  ElMessage.success('添加授权成功')
  initFeeLogDetail()
  getPermissionList()
}

const handlePersonSelectConfirm = () => {
  handleAddPermission(selectedUsers.value)
}

const resetPendingCancelUser = () => {
  pendingCancelUser.value = null
  confirmVisible.value = false
}

const openCancelConfirm = (row) => {
  if (!aiLicenseManagementEdit.value) {
    showNoPermissionMessage()
    return
  }

  if (!row?.userId) {
    return
  }

  pendingCancelUser.value = row
  confirmVisible.value = true
}

const handleConfirmCancel = () => {
  resetPendingCancelUser()
}

const handleConfirmPermissionCancel = async () => {
  const row = pendingCancelUser.value

  if (!row?.userId) {
    resetPendingCancelUser()
    return
  }

  cancelLoading.value = true
  try {
    await deleteLicense({
      license: 74,
      userIdList: [row.userId]
    })
    ElMessage.success('取消授权成功')
    resetPendingCancelUser()
    initFeeLogDetail()
    getPermissionList()
  } finally {
    cancelLoading.value = false
  }
}

const handlePageChange = (page) => {
  currentPage.value = page
  getPermissionList()
}
const initFeeLogDetail = async () => {
  const res = await getFeeLogDetail()
  const aifeeData = res.result.find((item) => item.feeType === 74)
  totalCount.value = aifeeData.sumValue
  hasAuthorize.value = aifeeData.sumValue - aifeeData.unUseValue
}

const initEditPermission = async () => {
  const res = await hasRoleEdit()
  aiLicenseManagementEdit.value = !!res?.result?.aiLicenseManagementEdit
}

onMounted(async () => {
  await initEditPermission()
  initFeeLogDetail()
  getPermissionList()
})
</script>

<style lang="scss" scoped>
.ai-assistant-permission {
  height: 100%;
  width: 100%;
  min-width: 634px;
  position: relative;
  display: flex;
  flex-direction: column;

  .title {
    font-family: PingFang SC;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    color: #323233;
  }

  .desc-card {
    margin-top: 16px;
    padding: 12px;
    border-radius: 12px;
    background: #f7f8fa;

    &__field {
      display: flex;
      flex-direction: column;
    }

    &__label {
      font-family: PingFang SC;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: #646566;
    }

    &__value {
      margin-top: 2px;
      font-family: PingFang SC;
      font-size: 20px;
      font-weight: 600;
      line-height: 28px;
    }

    &__value-current {
      color: #323233;
    }

    &__value-split,
    &__value-total {
      color: #969799;
    }

    &__progress-track {
      margin-top: 8px;
      width: 100%;
      height: 6px;
      overflow: hidden;
      border-radius: 50px;
      background: #e5e6eb;
    }

    &__progress-bar {
      height: 100%;
      border-radius: 50px;
      background: #7a52f4;
    }
  }

  &--init {
    .desc-card {
      margin-top: 0;
    }
  }

  .condition {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-top: 24px;
    align-items: center;

    &__filters {
      display: flex;
      align-items: flex-start;
      flex: 1;
    }

    &__item {
      width: 240px;
      margin-right: 12px;
    }

    &__action {
      flex: 0 0 auto;
      border-radius: 8px;
    }
  }

  .permission-list {
    margin-top: 24px;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &__table-wrap {
      flex: 1 1 auto;
      min-height: 0;
      overflow: hidden;
    }

    &__table {
      width: 100%;
      height: 100%;
      --el-table-border-color: #f2f3f5;
      --el-table-header-bg-color: #f7f8fa;
      --el-table-row-hover-bg-color: #faf7ff;
      --el-table-header-text-color: #646566;
      --el-table-text-color: #323233;
    }

    &__pagination {
      flex: 0 0 auto;
      margin-top: 12px;
      display: flex;
      justify-content: flex-end;
    }

    &__cancel-btn {
      padding: 0;
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
      color: #248df5;
    }

    :deep(.permission-list__header-cell) {
      height: 40px;
      padding: 12px;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      background: #f7f8fa;
    }

    :deep(.permission-list__cell) {
      height: 40px;
      padding: 12px;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      color: #323233;
    }

    :deep(.el-table th.el-table__cell.is-leaf),
    :deep(.el-table td.el-table__cell) {
      border-bottom: 1px solid #f2f3f5;
    }

    :deep(.el-table::before) {
      display: none;
    }

    :deep(.el-table__inner-wrapper) {
      height: 100%;
    }

    :deep(.el-pagination.is-background .el-pager li.is-active) {
      color: #7a52f4;
      background: transparent;
    }
  }

  :deep(.dialog-footer) {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}
</style>
